import React, { useEffect, useRef, useState } from 'react';
import { 
  Button, 
  FormControl, 
  Grid, 
  TextField, 
  Typography, 
  Paper, 
  Divider, 
  Stack, 
  Box, 
  Container 
} from '@mui/material';
import { 
  Download as DownloadIcon, 
  QrCode as QrIcon, 
  Close as CloseIcon 
} from '@mui/icons-material';

/**
 * --- INTERNAL MOCK IMPLEMENTATIONS ---
 */

// Mock for IntegrationTab
const IntegrationTab = () => (
    <Box sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px dashed #ccc' }}>
        <Typography variant="caption" color="text.secondary">Integration Tab Placeholder</Typography>
    </Box>
);

// Mock for Hooks/API
const useAuth = () => ({ Logout: () => console.log("Logout triggered") });
const getOnlineInfo = async () => ({ code: 0, data: { title: 'MiiRA Store', description: 'Best POS Store', socialLinks: {} } });
const updateOnlineInfo = async (data) => ({ code: 0, data });

/**
 * UPDATED: Scannable QRCodeCanvas Mock
 * This uses a QR API to generate a real, scannable pattern and draws it to a canvas.
 * This ensures the "Download PNG" button works and the QR is readable by phones.
 * TO USE IN PRODUCTION: 
 * 1. npm install qrcode.react
 * 2. import { QRCodeCanvas } from 'qrcode.react';
 */
const QRCodeCanvas = ({ value, size, id }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      const img = new Image();
      // Using a public API to get a real scannable QR pattern
      img.crossOrigin = "anonymous"; // Essential for canvas download (toDataURL)
      img.src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&margin=10`;
      
      img.onload = () => {
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
      };
      
      // Initial placeholder state
      ctx.fillStyle = '#f9f9f9';
      ctx.fillRect(0, 0, size, size);
    }
  }, [size, value]);

  return (
    <canvas 
      ref={canvasRef} 
      id={id} 
      width={size} 
      height={size} 
      style={{ 
        width: size, 
        height: size, 
        border: '4px solid #1a1b2f', 
        borderRadius: '12px',
        backgroundColor: '#fff',
        display: 'block'
      }} 
    />
  );
};

// --------------------------------------------------------------------------------

export const Online = () => {
    const [editMode, setEditMode] = useState(false);
    const storeID = useRef(JSON.parse(window.localStorage.getItem('ds')));
    const { Logout } = useAuth();
    
    const [onlineInfo, setOnlineInfo] = useState({
        storeID: storeID.current,
        title: '',
        description: '',
        favicon: '',
        logo: '',
        bHeading: '',
        bSubTitle: '',
        bParaggraph: '',
        bImage: '',
        socialLinks: {
            facebook: '',
            instagram: '',
            youtube: '',
            twitter: '',
            linkedin: ''
        }
    });

    // The live URL that will be encoded in the QR code
    const storeMenuUrl = `https://stores.miirapos.com/${storeID.current}/menu`; // Example: https://stores.miirapos.com/12345/menu

    /**
     * handleDownloadQR
     * Targets the canvas, converts to PNG, and triggers download.
     */
    const handleDownloadQR = () => {
        const canvas = document.getElementById("store-qr-canvas");
        if (!canvas) return;

        try {
            const url = canvas.toDataURL("image/png");
            const link = document.createElement("a");
            link.href = url;
            link.download = `${onlineInfo.title || 'MiiRA-Store'}-Menu-QR.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Download failed:", err);
            alert("Unable to generate download. Please ensure you are using a modern browser.");
        }
    };

    async function onlineInfoFetcher() {
        try {
            const response = await getOnlineInfo({ storeID: onlineInfo.storeID }, Logout);
            if (response && response.code === 0) {
                setOnlineInfo(prev => ({ ...prev, ...response.data }));
            }
        } catch (err) {
            console.error("Failed to fetch online info", err);
        }
    }

    useEffect(() => {
        onlineInfoFetcher();
    }, []);

    return (
        <Container maxWidth={false} sx={{ width: '100%', position: 'relative', pb: 5 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>Integration Guide</Typography>
            <IntegrationTab />

            <Box sx={{ mt: 3 }}>
                <Grid container spacing={3} justifyContent="space-between" alignItems="flex-start">
                    {/* Form Section */}
                    <Grid item xs={12} md={8.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" fontWeight="bold">Online Information</Typography>
                            <Box>
                                {editMode ? (
                                    <Stack direction="row" spacing={1}>
                                        <Button size="small" variant="text" onClick={() => setEditMode(false)}>Cancel</Button>
                                        <Button size="small" variant="contained" color="primary" onClick={async () => {
                                            const resp = await updateOnlineInfo(onlineInfo, Logout);
                                            if (resp.code === 0) setEditMode(false);
                                        }}>Save Changes</Button>
                                    </Stack>
                                ) : (
                                    <Button size="small" variant="outlined" onClick={() => setEditMode(true)}>Edit Details</Button>
                                )}
                            </Box>
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}><Typography variant="body2" fontWeight="500">SEO Title:</Typography></Grid>
                            <Grid item xs={12} sm={9}>
                                <TextField fullWidth size="small" variant="outlined" value={onlineInfo.title} disabled={!editMode}
                                    onChange={(e) => setOnlineInfo({ ...onlineInfo, title: e.target.value })} />
                            </Grid>

                            <Grid item xs={12} sm={3}><Typography variant="body2" fontWeight="500">Store Description:</Typography></Grid>
                            <Grid item xs={12} sm={9}>
                                <TextField fullWidth size="small" multiline rows={2} value={onlineInfo.description} disabled={!editMode}
                                    onChange={(e) => setOnlineInfo({ ...onlineInfo, description: e.target.value })} />
                            </Grid>

                            <Grid item xs={12} sm={3}><Typography variant="body2" fontWeight="500">Favicon (File):</Typography></Grid>
                            <Grid item xs={12} sm={9}><TextField type="file" fullWidth size="small" disabled={!editMode} InputLabelProps={{ shrink: true }} /></Grid>

                            <Grid item xs={12} sm={3}><Typography variant="body2" fontWeight="500">Brand Logo (File):</Typography></Grid>
                            <Grid item xs={12} sm={9}><TextField type="file" fullWidth size="small" disabled={!editMode} InputLabelProps={{ shrink: true }} /></Grid>
                        </Grid>

                        <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 1 }}>Home Page Banner</Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}><Typography variant="body2" fontWeight="500">Heading:</Typography></Grid>
                            <Grid item xs={12} sm={9}>
                                <TextField fullWidth size="small" value={onlineInfo.bHeading} disabled={!editMode}
                                    onChange={(e) => setOnlineInfo({ ...onlineInfo, bHeading: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} sm={3}><Typography variant="body2" fontWeight="500">Sub-Paragraph:</Typography></Grid>
                            <Grid item xs={12} sm={9}>
                                <TextField fullWidth size="small" multiline rows={2} value={onlineInfo.bParaggraph} disabled={!editMode}
                                    onChange={(e) => setOnlineInfo({ ...onlineInfo, bParaggraph: e.target.value })} />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* QR Code Sidebar - UPDATED TO BE SCANNABLE */}
                    <Grid item xs={12} md={3}>
                        <Paper 
                            variant="outlined" 
                            sx={{ 
                                p: 2.5, 
                                textAlign: 'center', 
                                borderRadius: 4, 
                                bgcolor: '#fcfcfc',
                                border: '1px solid #e0e0e0',
                                position: 'sticky',
                                top: 20
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight="900" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <QrIcon fontSize="small" color="primary" /> STORE QR CODE
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <QRCodeCanvas 
                                    id="store-qr-canvas"
                                    value={storeMenuUrl} 
                                    size={180} 
                                />
                            </Box>
                            
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, lineHeight: 1.4 }}>
                                This QR is now live and scannable. Print it and place it on your tables for instant menu access.
                            </Typography>
                            
                            <Button 
                                fullWidth 
                                variant="contained" 
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadQR}
                                sx={{ 
                                    borderRadius: 2, 
                                    textTransform: 'none', 
                                    fontWeight: 'bold',
                                    bgcolor: '#1a1b2f',
                                    '&:hover': { bgcolor: 'tomato' }
                                }}
                            >
                                Download PNG
                            </Button>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Social Links Section */}
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Social Presence</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                        {Object.keys(onlineInfo.socialLinks).map((platform) => (
                            <React.Fragment key={platform}>
                                <Grid item xs={12} sm={2}>
                                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{platform}:</Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField 
                                        fullWidth size="small" 
                                        placeholder={`${platform} URL`}
                                        value={onlineInfo.socialLinks[platform] || ''} 
                                        disabled={!editMode}
                                        onChange={(e) => setOnlineInfo({ 
                                            ...onlineInfo, 
                                            socialLinks: { ...onlineInfo.socialLinks, [platform]: e.target.value } 
                                        })} 
                                    />
                                </Grid>
                            </React.Fragment>
                        ))}
                    </Grid>
                </Box>
            </Box>

            {/* Feature Coming Soon Overlay */}
            <Box sx={{ 
                position: 'absolute', width: '100%', height: '100%', 
                background: 'rgba(255, 255, 255, 0.9)', left: 0, top: 0, 
                zIndex: 100, display: "flex", justifyContent: 'center', 
                alignItems: 'center', backdropFilter: 'blur(2px)'
            }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 5, textAlign: 'center', maxWidth: 400 }}>
                    <Typography variant="h4" fontWeight="900" color="primary" gutterBottom>
                        Coming Soon
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        The <b>MiiRA Website Builder</b> is currently in beta. This dashboard will be unlocked shortly.
                    </Typography>
                </Paper>
            </Box>
        </Container>
    );
};

export default Online;